import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import i18n, { STORAGE_KEY_LANG, SupportedLanguage } from 'shared/i18n/i18n';

interface LanguageDescriptor {
  code: SupportedLanguage;
  label: string;
  nativeLabel: string;
  dir: 'rtl' | 'ltr';
}

interface LanguageContextValue {
  language: SupportedLanguage;
  dir: 'rtl' | 'ltr';
  setLanguage: (lang: SupportedLanguage) => void;
  availableLanguages: LanguageDescriptor[];
}

const LANGUAGES: LanguageDescriptor[] = [
  { code: 'he', label: 'Hebrew', nativeLabel: 'עברית', dir: 'rtl' },
  { code: 'ar', label: 'Arabic', nativeLabel: 'العربية', dir: 'rtl' },
  { code: 'en', label: 'English', nativeLabel: 'English', dir: 'ltr' },
];

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

const directionFor = (lang: SupportedLanguage): 'rtl' | 'ltr' =>
  lang === 'en' ? 'ltr' : 'rtl';

const applyDirection = (lang: SupportedLanguage) => {
  if (typeof document === 'undefined') return;
  const dir = directionFor(lang);
  document.documentElement.setAttribute('dir', dir);
  document.documentElement.setAttribute('lang', lang);
  document.body.setAttribute('dir', dir);
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { i18n: i18nInstance } = useTranslation();
  const [language, setLanguageState] = useState<SupportedLanguage>(
    (i18nInstance.language as SupportedLanguage) || 'he',
  );

  useEffect(() => {
    applyDirection(language);
  }, [language]);

  const setLanguage = useCallback(
    (lang: SupportedLanguage) => {
      i18n.changeLanguage(lang);
      localStorage.setItem(STORAGE_KEY_LANG, lang);
      setLanguageState(lang);
      applyDirection(lang);
    },
    [],
  );

  const value = useMemo<LanguageContextValue>(
    () => ({
      language,
      dir: directionFor(language),
      setLanguage,
      availableLanguages: LANGUAGES,
    }),
    [language, setLanguage],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = (): LanguageContextValue => {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error('useLanguage must be used inside LanguageProvider');
  }
  return ctx;
};
